import { Boxes, Landmark, Receipt, ShoppingCart } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { ErrorCard, LoadingBlock, Pill } from "../AdminBits";
import useAdminFetch from "../../../hooks/useAdminFetch";
import { BANK_TRANSACTION_TYPE, BORDEREAU_STATUS, CREDIT_NOTE_STATUS, INVENTORY_ZONE, QUOTE_STATUS, RETURN_REASON, SHELF_TYPE, ZONE_LABELS, CASH_STATUS, CREDIT_STATUS, EXPIRY_STATE, INSURER_TYPE, INVENTORY_KIND, INVENTORY_MODE, INVENTORY_STATUS, INVOICE_STATUS, ISSUED_INVOICE_STATUS, ISSUED_INVOICE_TYPE, MOVEMENT_TYPES, OBJECTIVE_METRIC, OBJECTIVE_STATUS, ORDER_STATUS, PAYMENT_LABELS, PERIOD_TYPE, SALE_STATUS, SCHEDULE_ACTION, SCHEDULE_STATUS, STOCK_STATE, SUPPLIER_PAYMENT_MODE, TEMPLATE_PERIOD, TRANSACTION_TYPES, dayLabel, formatCount, formatMoney, toneOf } from "../../../lib/jamoraAdmin";
import { formatDateTime } from "../../../lib/tickets";

const pill = (map, code) => { const [text, tone] = toneOf(map, code); return <Pill tone={tone}>{text}</Pill>; };
const optional = (value) => value || "—";
const yesNo = (value) => (value ? "Oui" : "Non");
const dateTime = (iso) => (iso ? formatDateTime(iso) : "—");
const tabsOf = (all, map, pick = (v) => v[0]) => [["", all], ...Object.entries(map).map(([code, value]) => [code, pick(value)])];

// One read-only list of a pharmacy's business data. `config`: { path, columns, rowKey, filter?: { param, label, tabs }, search?, noun, empty }.
export function Lines({ schema, kind, row, onClose }) {
  const path = row ? `/pharmacies/${schema}/${kind}/${row.id}/lines` : null;
  const { phase, data, error, reload } = useAdminFetch(path, undefined, { enabled: !!row });
  const money = (v) => formatMoney(v);
  return (
    <Dialog open={!!row} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="lines-dialog">
        <DialogHeader>
          <DialogTitle className="text-left">{kind === "sales" ? `Lignes de la vente n° ${row?.id}` : `Lignes de la commande ${row?.number || ""}`}</DialogTitle>
          <DialogDescription className="text-left">Produits, quantités et prix. Rien sur la personne servie.</DialogDescription>
        </DialogHeader>
        {phase === "error" && <ErrorCard message={error} onRetry={reload} testid="lines-error" />}
        {(phase === "loading" || phase === "idle") && <LoadingBlock rows={3} />}
        {phase === "ready" && (
          <div className="max-h-[60vh] overflow-y-auto" data-testid="lines-list">
            <ul className="divide-y divide-slate-100 text-sm">
              {data.items.map((line) => (
                <li key={line.id} className="py-2 flex items-start justify-between gap-3" data-testid={`line-${line.id}`}>
                  <span className="min-w-0"><span className="font-medium text-slate-900 break-words">{line.name}</span><span className="block text-xs text-slate-500">{[line.code, line.unit, line.free_unit && "unité gratuite", line.returned && "retournée"].filter(Boolean).join(" · ")}</span></span>
                  <span className="text-right shrink-0 tabular-nums">{kind === "sales" ? `${formatCount(line.quantity)} × ${money(line.unit_price)}` : `${formatCount(line.received)} reçus sur ${formatCount(line.quantity)}`}<span className="block font-medium text-slate-900">{money(line.total)}</span></span>
                </li>
              ))}
              {!data.items.length && <li className="py-3 text-slate-500">Aucune ligne.</li>}
            </ul>
            {data.truncated && <p className="text-xs text-slate-500 pt-2">Liste tronquée aux 500 premières lignes.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const CONFIGS = {
  medicaments: {
    path: "medicaments", noun: "médicament(s)", icon: Boxes, empty: "Aucun médicament", search: "Nom, code, DCI…", rowKey: (m) => m.id,
    filters: [{ param: "active", label: "État", tabs: [["", "Tous"], ["true", "Actifs"], ["false", "Archivés"]] },
              { param: "sort", label: "Tri", tabs: [["", "Par nom"], ["sold", "Les plus vendus"]] }],
    columns: [
      { id: "name", label: "Médicament", primary: true, cell: (m) => <span>{m.name}<span className="block text-xs text-slate-500">{[m.code, m.dosage].filter(Boolean).join(" · ")}</span></span> },
      { id: "sold", label: "Vendus (30 j)", cell: (m) => (m.sold_30d === undefined ? "—" : `${formatCount(m.sold_30d)} boîtes`) },
      { id: "category", label: "Catégorie", hide: "lg", cell: (m) => optional(m.category) },
      { id: "sale", label: "Prix de vente", cell: (m) => formatMoney(m.sale_price) },
      { id: "purchase", label: "Prix d'achat", hide: "lg", cell: (m) => formatMoney(m.purchase_price) },
      { id: "flags", label: "Particularités", cell: (m) => (m.rx || m.narcotic || m.sensitive ? <span className="inline-flex flex-wrap gap-1">{m.rx && <Pill tone="sky">Ordonnance</Pill>}{m.narcotic && <Pill tone="red">Stupéfiant</Pill>}{m.sensitive && <Pill tone="amber">Sensible</Pill>}</span> : "—") },
      { id: "expires", label: "Expiration", hide: "lg", cell: (m) => dayLabel(m.expires_on) },
      { id: "active", label: "État", hide: "lg", cell: (m) => <Pill tone={m.active ? "emerald" : "slate"}>{m.active ? "Actif" : "Archivé"}</Pill> },
    ],
  },
  stock: {
    path: "stock", noun: "ligne(s)", icon: Boxes, empty: "Aucune ligne de stock", search: "Nom ou code…", rowKey: (s) => s.id,
    reserveField: "reserve",
    filters: [{ param: "state", label: "État du stock", tabs: tabsOf("Tous", STOCK_STATE) },
              { param: "in_reserve", label: "Rangement", reserve: true, tabs: [["", "Tout"], ["true", "En réserve"]] }],
    columns: (withReserve) => [
      { id: "name", label: "Médicament", primary: true, cell: (s) => <span>{s.name}<span className="block text-xs text-slate-500">{[s.code, s.dosage].filter(Boolean).join(" · ")}</span></span> },
      { id: "quantity", label: "Quantité", cell: (s) => formatCount(s.quantity) },
      ...(withReserve ? [
        { id: "room", label: "En salle", cell: (s) => formatCount(s.room) },
        { id: "reserve", label: "En réserve", cell: (s) => formatCount(s.reserve) },
      ] : []),
      { id: "minimum", label: "Minimum", hide: "lg", cell: (s) => formatCount(s.minimum) },
      { id: "state", label: "État", cell: (s) => pill(STOCK_STATE, s.state) },
      { id: "expires", label: "Expiration", hide: "lg", cell: (s) => dayLabel(s.expires_on) },
      { id: "updated", label: "Mis à jour", hide: "xl", cell: (s) => dateTime(s.updated_at) },
    ],
  },
  shelves: {
    path: "shelves", noun: "rayon(s)", icon: Boxes, empty: "Aucun rayon", rowKey: (r) => r.id,
    filters: [{ param: "type", label: "Type", tabs: tabsOf("Tous", SHELF_TYPE) },
              { param: "active", label: "État", tabs: [["", "Tous"], ["true", "Actifs"], ["false", "Désactivés"]] }],
    columns: [
      { id: "name", label: "Rayon", primary: true, cell: (r) => <span>{r.name}{r.parent && <span className="block text-xs text-slate-500">dans {r.parent}</span>}</span> },
      { id: "type", label: "Type", cell: (r) => pill(SHELF_TYPE, r.type) },
      { id: "products", label: "Produits rangés", cell: (r) => formatCount(r.products) },
      { id: "active", label: "État", hide: "lg", cell: (r) => <Pill tone={r.active ? "emerald" : "slate"}>{r.active ? "Actif" : "Désactivé"}</Pill> },
    ],
  },
  "stock-movements": {
    path: "stock-movements", noun: "mouvement(s)", icon: Boxes, empty: "Aucun mouvement", rowKey: (m) => m.id,
    reserveField: "zone",
    filters: [{ param: "type", label: "Type", tabs: tabsOf("Tous", MOVEMENT_TYPES, (v) => v) },
              { param: "zone", label: "Zone", reserve: true, tabs: [["", "Toutes"], ...Object.entries(ZONE_LABELS)] }],
    columns: (withReserve) => [
      { id: "name", label: "Médicament", primary: true, cell: (m) => m.name },
      { id: "type", label: "Type", cell: (m) => MOVEMENT_TYPES[m.type] || m.type },
      { id: "quantity", label: "Quantité", cell: (m) => (m.quantity > 0 ? `+${formatCount(m.quantity)}` : formatCount(m.quantity)) },
      ...(withReserve ? [{ id: "zone", label: "Zone", cell: (m) => (m.type === "TRANSFERT" ? `vers ${(ZONE_LABELS[m.zone] || m.zone).toLowerCase()}` : ZONE_LABELS[m.zone] || m.zone) }] : []),
      { id: "reference", label: "Origine", hide: "lg", cell: (m) => optional(m.reference_type) },
      { id: "at", label: "Date", cell: (m) => dateTime(m.at) },
    ],
  },
  sales: {
    path: "sales", noun: "vente(s)", icon: Receipt, empty: "Aucune vente", rowKey: (s) => s.id, lines: "sales",
    filter: { param: "payment", label: "Paiement", tabs: tabsOf("Tous", PAYMENT_LABELS, (v) => v).filter(([code]) => ["", "CASH", "CARD", "MOBILE", "WAVE", "ORANGE_MONEY", "CREDIT", "TIERS_PAYANT"].includes(code)) },
    columns: [
      { id: "id", label: "Vente", primary: true, cell: (s) => `n° ${s.id}` },
      { id: "at", label: "Date", cell: (s) => dateTime(s.at) },
      { id: "seller", label: "Vendeur", cell: (s) => optional(s.seller) },
      { id: "payment", label: "Paiement", cell: (s) => PAYMENT_LABELS[s.payment] || s.payment },
      { id: "lines", label: "Lignes", hide: "lg", cell: (s) => (s.lines === undefined ? "—" : formatCount(s.lines)) },
      { id: "units", label: "Boîtes", cell: (s) => (s.units === undefined ? "—" : <span>{formatCount(s.units)}{s.loose_units > 0 && <span className="block text-xs text-slate-500">+ {formatCount(s.loose_units)} détachées</span>}</span>) },
      { id: "total", label: "Total", cell: (s) => formatMoney(s.total) },
      { id: "tax", label: "TVA", hide: "lg", cell: (s) => formatMoney(s.tax) },
      { id: "insured", label: "Assurance", hide: "xl", cell: (s) => yesNo(s.insured) },
      { id: "status", label: "Statut", hide: "lg", cell: (s) => pill(SALE_STATUS, s.status) },
    ],
  },
  "cash-sessions": {
    path: "cash-sessions", noun: "session(s)", icon: Receipt, empty: "Aucune session de caisse", rowKey: (a) => a.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Toutes", CASH_STATUS) },
    columns: [
      { id: "register", label: "Caisse", primary: true, cell: (a) => <span>{optional(a.cash_register)}<span className="block text-xs text-slate-500">{optional(a.seller)}</span></span> },
      { id: "status", label: "Statut", cell: (a) => pill(CASH_STATUS, a.status) },
      { id: "opened", label: "Ouverture", cell: (a) => dateTime(a.opened_at) },
      { id: "closed", label: "Fermeture", hide: "lg", cell: (a) => dateTime(a.closed_at) },
      { id: "fund", label: "Fond", hide: "xl", cell: (a) => formatMoney(a.opening_fund) },
      { id: "expected", label: "Solde théorique", hide: "lg", cell: (a) => formatMoney(a.expected_balance) },
      { id: "actual", label: "Solde réel", hide: "lg", cell: (a) => formatMoney(a.actual_balance) },
      { id: "gap", label: "Écart", cell: (a) => <span className={a.gap ? (a.gap < 0 ? "text-red-700" : "text-sky-700") : ""}>{formatMoney(a.gap)}{a.corrected && " (corrigé)"}</span> },
    ],
  },
  orders: {
    path: "orders", noun: "commande(s)", icon: Landmark, empty: "Aucune commande", rowKey: (o) => o.id, lines: "orders",
    filter: { param: "status", label: "Statut", tabs: tabsOf("Toutes", ORDER_STATUS) },
    columns: [
      { id: "number", label: "Commande", primary: true, cell: (o) => o.number },
      { id: "supplier", label: "Fournisseur", cell: (o) => optional(o.supplier) },
      { id: "status", label: "Statut", cell: (o) => <span className="inline-flex flex-wrap gap-1">{pill(ORDER_STATUS, o.status)}{o.shortage && <Pill tone="amber">Manque à la réception</Pill>}</span> },
      { id: "boxes", label: "Boîtes reçues", cell: (o) => (o.ordered === undefined ? "—" : <span>{formatCount(o.received)} sur {formatCount(o.ordered)}<span className="block text-xs text-slate-500">{formatCount(o.lines)} ligne(s)</span></span>) },
      { id: "total", label: "Total", cell: (o) => formatMoney(o.total) },
      { id: "created", label: "Créée le", cell: (o) => dateTime(o.created_at) },
      { id: "expected", label: "Livraison prévue", hide: "lg", cell: (o) => dayLabel(o.expected_on) },
      { id: "received", label: "Reçue le", hide: "lg", cell: (o) => dayLabel(o.received_on) },
    ],
  },
  "supplier-invoices": {
    path: "supplier-invoices", noun: "facture(s)", icon: Landmark, empty: "Aucune facture fournisseur", rowKey: (f) => f.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Toutes", INVOICE_STATUS) },
    columns: [
      { id: "number", label: "Facture", primary: true, cell: (f) => f.number },
      { id: "supplier", label: "Fournisseur", cell: (f) => optional(f.supplier) },
      { id: "status", label: "Statut", cell: (f) => pill(INVOICE_STATUS, f.status) },
      { id: "boxes", label: "Boîtes facturées", cell: (f) => (f.units === undefined || f.units === null ? "—" : <span>{formatCount(f.units)}<span className="block text-xs text-slate-500">{formatCount(f.lines)} ligne(s)</span></span>) },
      { id: "total", label: "Total", cell: (f) => formatMoney(f.total) },
      { id: "dated", label: "Date", hide: "lg", cell: (f) => dayLabel(f.dated) },
      { id: "due", label: "Échéance", cell: (f) => dayLabel(f.due) },
    ],
  },
  "supplier-returns": {
    path: "supplier-returns", noun: "retour(s)", icon: Landmark, empty: "Aucun retour fournisseur", rowKey: (r) => r.id,
    columns: [
      { id: "number", label: "Retour", primary: true, cell: (r) => r.number },
      { id: "supplier", label: "Fournisseur", cell: (r) => optional(r.supplier) },
      { id: "reason", label: "Motif", cell: (r) => RETURN_REASON[r.reason] || r.reason },
      { id: "units", label: "Boîtes retournées", cell: (r) => <span>{formatCount(r.units)}<span className="block text-xs text-slate-500">{formatCount(r.lines)} ligne(s)</span></span> },
      { id: "value", label: "Valeur", cell: (r) => formatMoney(r.value) },
      { id: "date", label: "Date", hide: "lg", cell: (r) => dayLabel(r.returned_on) },
      { id: "reminders", label: "Relances", hide: "xl", cell: (r) => formatCount(r.reminders) },
    ],
  },
  "supplier-credit-notes": {
    path: "supplier-credit-notes", noun: "avoir(s)", icon: Landmark, empty: "Aucun avoir fournisseur", rowKey: (a) => a.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Tous", CREDIT_NOTE_STATUS) },
    columns: [
      { id: "number", label: "Avoir", primary: true, cell: (a) => a.number },
      { id: "supplier", label: "Fournisseur", cell: (a) => optional(a.supplier) },
      { id: "status", label: "Statut", cell: (a) => pill(CREDIT_NOTE_STATUS, a.status) },
      { id: "amount", label: "Montant", cell: (a) => formatMoney(a.amount) },
      { id: "units", label: "Boîtes du retour", cell: (a) => <span>{formatCount(a.units)}<span className="block text-xs text-slate-500">retour {optional(a.return_number)}</span></span> },
      { id: "received", label: "Reçu le", hide: "lg", cell: (a) => dayLabel(a.received_on) },
      { id: "settled", label: "Régularisé le", hide: "xl", cell: (a) => dayLabel(a.settled_on) },
    ],
  },
  quotes: {
    path: "quotes", noun: "devis", icon: Receipt, empty: "Aucun devis", rowKey: (q) => q.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Tous", QUOTE_STATUS) },
    columns: [
      { id: "number", label: "Devis", primary: true, cell: (q) => q.number },
      { id: "created", label: "Date", cell: (q) => dateTime(q.created_at) },
      { id: "status", label: "Statut", cell: (q) => pill(QUOTE_STATUS, q.status) },
      { id: "units", label: "Boîtes", cell: (q) => <span>{formatCount(q.units)}<span className="block text-xs text-slate-500">{formatCount(q.lines)} ligne(s)</span></span> },
      { id: "total", label: "Total indicatif", cell: (q) => formatMoney(q.total) },
      { id: "cancelled", label: "Annulé le", hide: "xl", cell: (q) => dateTime(q.cancelled_at) },
    ],
  },
  transactions: {
    path: "transactions", noun: "transaction(s)", icon: Landmark, empty: "Aucune transaction", rowKey: (t) => t.id,
    filter: { param: "type", label: "Type", tabs: tabsOf("Toutes", TRANSACTION_TYPES) },
    columns: [
      { id: "date", label: "Date", primary: true, cell: (t) => dayLabel(t.date) },
      { id: "type", label: "Type", cell: (t) => pill(TRANSACTION_TYPES, t.type) },
      { id: "category", label: "Catégorie", cell: (t) => optional(t.category) },
      { id: "amount", label: "Montant", cell: (t) => formatMoney(t.amount) },
      { id: "method", label: "Moyen de paiement", hide: "lg", cell: (t) => PAYMENT_LABELS[t.payment_method] || optional(t.payment_method) },
      { id: "reference", label: "Origine", hide: "xl", cell: (t) => optional(t.reference_type) },
    ],
  },
  bordereaux: {
    path: "bordereaux", noun: "bordereau(x)", icon: Landmark, empty: "Aucun bordereau", rowKey: (b) => b.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Tous", BORDEREAU_STATUS) },
    columns: [
      { id: "reference", label: "Bordereau", primary: true, cell: (b) => b.reference },
      { id: "entity", label: "Organisme", cell: (b) => optional(b.entity) },
      { id: "status", label: "Statut", cell: (b) => pill(BORDEREAU_STATUS, b.status) },
      { id: "period", label: "Période", hide: "lg", cell: (b) => (b.month && b.year ? `${String(b.month).padStart(2, "0")}/${b.year}` : "—") },
      { id: "sheets", label: "Feuilles", hide: "xl", cell: (b) => formatCount(b.sheets) },
      { id: "total", label: "Total", cell: (b) => formatMoney(b.total) },
      { id: "insurance", label: "Part assurance", hide: "lg", cell: (b) => formatMoney(b.insurance) },
      { id: "reimbursed", label: "Remboursé", hide: "lg", cell: (b) => formatMoney(b.reimbursed) },
    ],
  },
  lots: {
    path: "lots", noun: "lot(s)", icon: Boxes, empty: "Aucun lot", search: "Produit, code ou numéro de lot…", rowKey: (l) => l.id,
    filter: { param: "expiry", label: "Péremption", tabs: tabsOf("Tous", EXPIRY_STATE) },
    columns: [
      { id: "name", label: "Produit", primary: true, cell: (l) => <span>{l.name}<span className="block text-xs text-slate-500">{l.code}</span></span> },
      { id: "lot", label: "Lot", cell: (l) => l.lot },
      { id: "quantity", label: "Quantité", cell: (l) => formatCount(l.quantity) },
      { id: "expires", label: "Expiration", cell: (l) => <span className="inline-flex flex-wrap items-center gap-1">{dayLabel(l.expires_on)}{pill(EXPIRY_STATE, l.expiry)}</span> },
      { id: "supplier", label: "Fournisseur", hide: "lg", cell: (l) => optional(l.supplier) },
      { id: "price", label: "Prix d'achat", hide: "xl", cell: (l) => formatMoney(l.purchase_price) },
    ],
  },
  "physical-inventories": {
    path: "physical-inventories", noun: "inventaire(s)", icon: Boxes, empty: "Aucun inventaire", rowKey: (i) => i.id,
    reserveField: "zone",
    filters: [{ param: "status", label: "Statut", tabs: tabsOf("Tous", INVENTORY_STATUS) },
              { param: "zone", label: "Zone comptée", reserve: true, tabs: [["", "Toutes"], ...Object.entries(INVENTORY_ZONE)] }],
    columns: (withReserve) => [
      { id: "started", label: "Début", primary: true, cell: (i) => dateTime(i.started_at) },
      ...(withReserve ? [{ id: "zone", label: "Zone", cell: (i) => INVENTORY_ZONE[i.zone] || i.zone }] : []),
      { id: "counted", label: "Produits comptés", cell: (i) => (i.items === undefined ? "—" : `${formatCount(i.items_counted)} sur ${formatCount(i.items)}`) },
      { id: "boxgap", label: "Écart (boîtes)", cell: (i) => (i.gap === undefined ? "—" : <span>{i.gap > 0 ? `+${formatCount(i.gap)}` : formatCount(i.gap)}<span className="block text-xs text-slate-500">{formatCount(i.counted)} comptées / {formatCount(i.expected)} attendues</span></span>) },
      { id: "status", label: "Statut", cell: (i) => <span className="inline-flex flex-wrap gap-1">{pill(INVENTORY_STATUS, i.status)}{i.reversed && <Pill tone="amber">Annulé après coup</Pill>}</span> },
      { id: "mode", label: "Mode", hide: "lg", cell: (i) => `${INVENTORY_KIND[i.kind] || i.kind} · ${INVENTORY_MODE[i.mode] || i.mode}` },
      { id: "ended", label: "Fin", hide: "lg", cell: (i) => dateTime(i.ended_at) },
      { id: "by", label: "Créé par", hide: "xl", cell: (i) => optional(i.created_by) },
      { id: "errors", label: "Erreurs", hide: "lg", cell: (i) => formatCount(i.errors) },
      { id: "value", label: "Valeur (achat)", cell: (i) => formatMoney(i.purchase_value) },
      { id: "gap", label: "Écarts (achat)", cell: (i) => <span>{formatMoney(i.negative_gap_purchase)} / {formatMoney(i.positive_gap_purchase)}</span> },
    ],
  },
  "cash-registers": {
    path: "cash-registers", noun: "caisse(s)", icon: Receipt, empty: "Aucune caisse", rowKey: (c) => c.id,
    filter: { param: "active", label: "État", tabs: [["", "Toutes"], ["true", "Actives"], ["false", "Désactivées"]] },
    columns: [
      { id: "name", label: "Caisse", primary: true, cell: (c) => c.name },
      { id: "active", label: "État", cell: (c) => <Pill tone={c.active ? "emerald" : "slate"}>{c.active ? "Active" : "Désactivée"}</Pill> },
      { id: "created", label: "Créée le", cell: (c) => dateTime(c.created_at) },
    ],
  },
  credits: {
    path: "credits", noun: "créance(s)", icon: Receipt, empty: "Aucune créance", rowKey: (c) => c.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Toutes", CREDIT_STATUS) },
    columns: [
      { id: "sale", label: "Vente", primary: true, cell: (c) => (c.sale_id ? `n° ${c.sale_id}` : "—") },
      { id: "status", label: "Statut", cell: (c) => pill(CREDIT_STATUS, c.status) },
      { id: "initial", label: "Montant", cell: (c) => formatMoney(c.initial) },
      { id: "repaid", label: "Remboursé", hide: "lg", cell: (c) => formatMoney(c.repaid) },
      { id: "remaining", label: "Reste dû", cell: (c) => formatMoney(c.remaining) },
      { id: "due", label: "Échéance", hide: "lg", cell: (c) => dayLabel(c.due_on) },
      { id: "reminders", label: "Relances", hide: "xl", cell: (c) => `${formatCount(c.reminders)}${c.last_reminder_at ? ` · dernière le ${dayLabel(c.last_reminder_at)}` : ""}` },
    ],
  },
  objectives: {
    path: "objectives", noun: "objectif(s)", icon: Receipt, empty: "Aucun objectif", rowKey: (o) => o.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Tous", OBJECTIVE_STATUS) },
    columns: [
      { id: "title", label: "Objectif", primary: true, cell: (o) => <span>{o.title}<span className="block text-xs text-slate-500">{OBJECTIVE_METRIC[o.metric] || o.metric}</span></span> },
      { id: "status", label: "Statut", cell: (o) => <span className="inline-flex flex-wrap gap-1">{pill(OBJECTIVE_STATUS, o.status)}{o.recurring && <Pill tone="sky">Récurrent</Pill>}</span> },
      { id: "target", label: "Cible", cell: (o) => formatCount(o.target) },
      { id: "period", label: "Périodicité", hide: "lg", cell: (o) => optional(o.period) },
      { id: "dates", label: "Période", hide: "lg", cell: (o) => `${dayLabel(o.starts_on)} → ${dayLabel(o.ends_on)}` },
    ],
  },
  suppliers: {
    path: "suppliers", noun: "fournisseur(s)", icon: ShoppingCart, empty: "Aucun fournisseur", search: "Nom ou code…", rowKey: (s) => s.id,
    filter: { param: "active", label: "État", tabs: [["", "Tous"], ["true", "Actifs"], ["false", "Désactivés"]] },
    columns: [
      { id: "name", label: "Fournisseur", primary: true, cell: (s) => <span>{s.name}<span className="block text-xs text-slate-500">{s.code}</span></span> },
      { id: "email", label: "E-mail", hide: "lg", cell: (s) => optional(s.email) },
      { id: "phone", label: "Téléphone", cell: (s) => optional(s.phone) },
      { id: "active", label: "État", cell: (s) => <Pill tone={s.active ? "emerald" : "slate"}>{s.active ? "Actif" : "Désactivé"}</Pill> },
    ],
  },
  "supplier-payments": {
    path: "supplier-payments", noun: "paiement(s)", icon: ShoppingCart, empty: "Aucun paiement fournisseur", rowKey: (p) => p.id,
    filter: { param: "mode", label: "Mode", tabs: tabsOf("Tous", SUPPLIER_PAYMENT_MODE, (v) => v) },
    columns: [
      { id: "invoice", label: "Facture", primary: true, cell: (p) => optional(p.invoice_number) },
      { id: "supplier", label: "Fournisseur", cell: (p) => optional(p.supplier) },
      { id: "amount", label: "Montant", cell: (p) => formatMoney(p.amount) },
      { id: "paid", label: "Payé le", cell: (p) => dayLabel(p.paid_on) },
      { id: "mode", label: "Mode", hide: "lg", cell: (p) => SUPPLIER_PAYMENT_MODE[p.mode] || p.mode },
      { id: "by", label: "Enregistré par", hide: "xl", cell: (p) => optional(p.recorded_by) },
    ],
  },
  invoices: {
    path: "invoices", noun: "facture(s)", icon: Landmark, empty: "Aucune facture émise", rowKey: (f) => f.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Toutes", ISSUED_INVOICE_STATUS) },
    columns: [
      { id: "number", label: "Facture", primary: true, cell: (f) => f.number },
      { id: "type", label: "Nature", hide: "lg", cell: (f) => ISSUED_INVOICE_TYPE[f.type] || f.type },
      { id: "entity", label: "Organisme", cell: (f) => optional(f.entity) },
      { id: "status", label: "Statut", cell: (f) => pill(ISSUED_INVOICE_STATUS, f.status) },
      { id: "total", label: "Total", cell: (f) => formatMoney(f.total) },
      { id: "issued", label: "Facturée le", hide: "lg", cell: (f) => dayLabel(f.issued_on) },
      { id: "due", label: "Échéance", cell: (f) => dayLabel(f.due_on) },
    ],
  },
  "bank-accounts": {
    path: "bank-accounts", noun: "compte(s)", icon: Landmark, empty: "Aucun compte bancaire", rowKey: (a) => a.id,
    filter: { param: "active", label: "État", tabs: [["", "Tous"], ["true", "Actifs"], ["false", "Désactivés"]] },
    columns: [
      { id: "name", label: "Compte", primary: true, cell: (a) => <span>{a.name}<span className="block text-xs text-slate-500">{optional(a.bank)}</span></span> },
      { id: "number", label: "Numéro", cell: (a) => <span className="font-mono text-xs">{optional(a.number)}</span> },
      { id: "opening", label: "Solde initial", cell: (a) => formatMoney(a.opening_balance) },
      { id: "active", label: "État", cell: (a) => <Pill tone={a.active ? "emerald" : "slate"}>{a.active ? "Actif" : "Désactivé"}</Pill> },
    ],
  },
  "bank-transactions": {
    path: "bank-transactions", noun: "opération(s)", icon: Landmark, empty: "Aucune opération bancaire", rowKey: (t) => t.id,
    filter: { param: "type", label: "Type", tabs: tabsOf("Toutes", BANK_TRANSACTION_TYPE) },
    columns: [
      { id: "date", label: "Date", primary: true, cell: (t) => dayLabel(t.date) },
      { id: "account", label: "Compte", cell: (t) => optional(t.account) },
      { id: "type", label: "Type", cell: (t) => pill(BANK_TRANSACTION_TYPE, t.type) },
      { id: "amount", label: "Montant", cell: (t) => formatMoney(t.amount) },
      { id: "session", label: "Session de caisse", hide: "lg", cell: (t) => (t.cash_session_id ? `n° ${t.cash_session_id}` : "—") },
    ],
  },
  "financial-periods": {
    path: "financial-periods", noun: "période(s)", icon: Landmark, empty: "Aucune période", rowKey: (p) => p.id,
    filter: { param: "closed", label: "Clôture", tabs: [["", "Toutes"], ["true", "Clôturées"], ["false", "Ouvertes"]] },
    columns: [
      { id: "name", label: "Période", primary: true, cell: (p) => <span>{p.name}<span className="block text-xs text-slate-500">{PERIOD_TYPE[p.type] || p.type}</span></span> },
      { id: "dates", label: "Du … au", cell: (p) => `${dayLabel(p.start)} → ${dayLabel(p.end)}` },
      { id: "closed", label: "État", cell: (p) => <Pill tone={p.closed ? "slate" : "emerald"}>{p.closed ? "Clôturée" : "Ouverte"}</Pill> },
      { id: "closed_at", label: "Clôturée le", hide: "lg", cell: (p) => dateTime(p.closed_at) },
      { id: "by", label: "Clôturée par", hide: "xl", cell: (p) => optional(p.closed_by) },
    ],
  },
  "finance-categories": {
    path: "finance-categories", noun: "catégorie(s)", icon: Landmark, empty: "Aucune catégorie", rowKey: (c) => c.id,
    filter: { param: "type", label: "Type", tabs: tabsOf("Toutes", TRANSACTION_TYPES) },
    columns: [
      { id: "name", label: "Catégorie", primary: true, cell: (c) => c.name },
      { id: "code", label: "Code", cell: (c) => optional(c.code) },
      { id: "type", label: "Type", cell: (c) => pill(TRANSACTION_TYPES, c.type) },
    ],
  },
  insurers: {
    path: "insurers", noun: "organisme(s)", icon: Landmark, empty: "Aucun organisme", search: "Nom ou code…", rowKey: (a) => a.id,
    filter: { param: "type", label: "Type", tabs: tabsOf("Tous", INSURER_TYPE, (v) => v) },
    columns: [
      { id: "name", label: "Organisme", primary: true, cell: (a) => <span>{a.name}<span className="block text-xs text-slate-500">{a.code}</span></span> },
      { id: "type", label: "Type", cell: (a) => INSURER_TYPE[a.type] || a.type },
      { id: "rate", label: "Prise en charge", cell: (a) => (a.coverage_rate === null || a.coverage_rate === undefined ? "—" : `${a.coverage_rate} %`) },
      { id: "delay", label: "Remboursement", hide: "lg", cell: (a) => (a.reimbursement_days ? `${a.reimbursement_days} jours` : "—") },
      { id: "deadline", label: "Envoi avant le", hide: "xl", cell: (a) => (a.send_deadline_day ? `${a.send_deadline_day} du mois` : "—") },
      { id: "channel", label: "Canal", hide: "xl", cell: (a) => optional(a.send_channel) },
      { id: "active", label: "État", cell: (a) => <Pill tone={a.active ? "emerald" : "slate"}>{a.active ? "Actif" : "Désactivé"}</Pill> },
    ],
  },
  "bordereau-templates": {
    path: "bordereau-templates", noun: "modèle(s)", icon: Landmark, empty: "Aucun modèle de bordereau", rowKey: (t) => t.id,
    filter: { param: "period", label: "Périodicité", tabs: tabsOf("Toutes", TEMPLATE_PERIOD, (v) => v) },
    columns: [
      { id: "name", label: "Modèle", primary: true, cell: (t) => <span>{t.name}{t.default && <Pill tone="sky"> Par défaut</Pill>}</span> },
      { id: "entity", label: "Organisme", cell: (t) => optional(t.entity) },
      { id: "period", label: "Périodicité", hide: "lg", cell: (t) => TEMPLATE_PERIOD[t.period] || t.period },
      { id: "format", label: "Format d'export", hide: "lg", cell: (t) => optional(t.export_format) },
      { id: "prefix", label: "Préfixe", hide: "xl", cell: (t) => optional(t.prefix) },
    ],
  },
  "bordereau-schedules": {
    path: "bordereau-schedules", noun: "planification(s)", icon: Landmark, empty: "Aucune planification", rowKey: (p) => p.id,
    filter: { param: "status", label: "Statut", tabs: tabsOf("Toutes", SCHEDULE_STATUS) },
    columns: [
      { id: "bordereau", label: "Bordereau", primary: true, cell: (p) => optional(p.bordereau) },
      { id: "action", label: "Action", cell: (p) => SCHEDULE_ACTION[p.action] || p.action },
      { id: "status", label: "Statut", cell: (p) => pill(SCHEDULE_STATUS, p.status) },
      { id: "planned", label: "Prévue le", cell: (p) => dateTime(p.planned_at) },
      { id: "executed", label: "Exécutée le", hide: "lg", cell: (p) => dateTime(p.executed_at) },
    ],
  },
};
